import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Users,
  UserPlus,
  Search,
  Shield,
  Mail,
  MoreVertical,
  Check,
  X,
  Loader2,
  AlertCircle,
  UserX,
  UserCheck,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Crown,
  ArrowRightLeft,
} from "lucide-react";
import { usersApi, rolesApi, billingApi, firmApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import SearchableSelect from "../ui/SearchableSelect";
import Toggle from "../ui/Toggle";
import {
  PERMISSION_LABELS,
  ROLE_DEFAULT_PERMISSIONS,
  type UserPermissions,
} from "../onboarding/steps/InviteTeamStep";

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  invited: { label: "Invited", className: "bg-amber-50 text-amber-700 border-amber-200" },
  disabled: { label: "Disabled", className: "bg-slate-100 text-slate-500 border-slate-200" },
};

const ROLE_OPTIONS = [
  { value: "partner", label: "Partner", description: "Full access" },
  { value: "associate", label: "Associate", description: "Clients, matters, documents" },
  { value: "paralegal", label: "Paralegal", description: "Case support" },
  { value: "clerk", label: "Law Clerk", description: "Research & review" },
  { value: "assistant", label: "Legal Assistant", description: "Scheduling, communications" },
  { value: "admin", label: "Office Admin", description: "Full administrative access" },
  { value: "bookkeeper", label: "Bookkeeper", description: "Financial management" },
];

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { userData, firmData } = useUserDataContext();

  // Team data
  const [members, setMembers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("associate");
  const [invitePermissions, setInvitePermissions] = useState<UserPermissions>(
    ROLE_DEFAULT_PERMISSIONS.associate
  );
  const [showPermissions, setShowPermissions] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Cost confirmation
  const [showCostConfirm, setShowCostConfirm] = useState(false);
  const [prorationData, setProrationData] = useState<any>(null);
  const [loadingProration, setLoadingProration] = useState(false);

  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  // Transfer ownership
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const [transferConfirmText, setTransferConfirmText] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  // Seat info
  const [seatData, setSeatData] = useState<{ seats: number; maxSeats: number | null; isTrial: boolean } | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      const params: Record<string, any> = { limit: 200 };
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await usersApi.list(params);
      setMembers(res.data?.data || res.data || []);
    } catch {
      // silent
    }
  }, [search, statusFilter]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await rolesApi.list();
      setRoles(res.data || []);
    } catch {
      // silent
    }
  }, []);

  const fetchSeats = useCallback(async () => {
    try {
      const res = await billingApi.getSeats();
      setSeatData(res.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchMembers(), fetchRoles(), fetchSeats()]);
      setLoading(false);
    };
    load();
  }, [fetchMembers, fetchRoles, fetchSeats]);

  const doInvite = async () => {
    setInviting(true);
    setInviteError(null);
    try {
      await usersApi.invite({
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        role: inviteRole,
        permissions: invitePermissions,
      });
      setInviteName("");
      setInviteEmail("");
      setInviteRole("associate");
      setInvitePermissions(ROLE_DEFAULT_PERMISSIONS.associate);
      setShowInvite(false);
      setShowPermissions(false);
      setShowCostConfirm(false);
      setProrationData(null);
      await Promise.all([fetchMembers(), fetchSeats()]);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.msg || "Failed to send invitation.";
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteName.trim()) return;

    const isOnPaidPlan = seatData && !seatData.isTrial && (seatData as any).plan !== "free trial";

    // If on paid plan and haven't confirmed cost yet, show confirmation first
    if (isOnPaidPlan && !showCostConfirm) {
      setLoadingProration(true);
      try {
        const res = await billingApi.getProrationPreview(1);
        setProrationData(res.data);
        setShowCostConfirm(true);
      } catch {
        // If preview fails, proceed anyway (graceful degradation)
        await doInvite();
      } finally {
        setLoadingProration(false);
      }
      return;
    }

    await doInvite();
  };

  const handleDeactivate = async (userId: string) => {
    setActionLoading(userId);
    try {
      await usersApi.deactivate(userId);
      await Promise.all([fetchMembers(), fetchSeats()]);
    } catch (err: any) {
      console.error("Deactivate failed:", err);
    } finally {
      setActionLoading(null);
      setMenuOpen(null);
    }
  };

  const handleReactivate = async (userId: string) => {
    setActionLoading(userId);
    try {
      await usersApi.reactivate(userId);
      await Promise.all([fetchMembers(), fetchSeats()]);
    } catch (err: any) {
      console.error("Reactivate failed:", err);
    } finally {
      setActionLoading(null);
      setMenuOpen(null);
    }
  };

  const handleResendInvite = async (user: any) => {
    setActionLoading(user._id);
    try {
      await usersApi.invite({
        email: user.email,
        name: user.name,
        role: user.role || "associate",
      });
    } catch (err: any) {
      console.error("Resend failed:", err);
    } finally {
      setActionLoading(null);
      setMenuOpen(null);
    }
  };

  const handleRoleChange = (role: string) => {
    setInviteRole(role);
    setInvitePermissions(ROLE_DEFAULT_PERMISSIONS[role] || ROLE_DEFAULT_PERMISSIONS.associate);
  };

  const handleTransferOwnership = async () => {
    if (!transferTarget) return;
    setTransferring(true);
    setTransferError(null);
    try {
      await firmApi.transferOwnership(transferTarget);
      setShowTransfer(false);
      setTransferTarget(null);
      setTransferConfirmText("");
      // Refresh to reflect new ownership
      window.location.reload();
    } catch (err: any) {
      setTransferError(err?.response?.data?.error || "Failed to transfer ownership");
    } finally {
      setTransferring(false);
    }
  };

  const activeCount = members.filter((m) => m.status === "active").length;

  const isOwner = userData?._id && firmData?.adminUserId && userData._id === firmData.adminUserId;
  const invitedCount = members.filter((m) => m.status === "invited").length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back to Settings</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
            <p className="text-sm text-slate-500 mt-1">
              Invite and manage your team members
            </p>
          </div>
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand text-white rounded-xl text-sm font-medium hover:bg-brand-hover transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            Invite Member
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-4"
        >
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Active</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{activeCount}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border border-slate-200 p-4"
        >
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Invited</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{invitedCount}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-200 p-4"
        >
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Seats</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">
            {seatData?.seats || members.length}
          </p>
          {seatData?.isTrial && (
            <p className="text-xs text-slate-400 mt-0.5">Free Trial</p>
          )}
        </motion.div>
      </div>

      {/* Invite Form */}
      <AnimatePresence>
        {showInvite && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-800">Invite New Team Member</h3>
                <button
                  onClick={() => {
                    setShowInvite(false);
                    setInviteError(null);
                    setShowPermissions(false);
                    setShowCostConfirm(false);
                    setProrationData(null);
                  }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Role</label>
                  <SearchableSelect
                    options={ROLE_OPTIONS}
                    value={inviteRole}
                    onChange={(val) => handleRoleChange(val || "associate")}
                    placeholder="Select role..."
                    searchPlaceholder="Search roles..."
                    icon={Shield}
                  />
                </div>
              </div>

              {/* Permissions toggle */}
              <button
                type="button"
                onClick={() => setShowPermissions(!showPermissions)}
                className="flex items-center gap-2 text-xs font-medium text-slate-500 hover:text-slate-700 mb-3 transition-colors"
              >
                <Shield className="h-3.5 w-3.5" />
                <span>Customize permissions</span>
                <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-[10px] font-bold text-slate-600">
                  {Object.values(invitePermissions).filter(Boolean).length} of {PERMISSION_LABELS.length}
                </span>
                {showPermissions ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>

              <AnimatePresence>
                {showPermissions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mb-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-4 bg-slate-50 rounded-xl">
                      {PERMISSION_LABELS.map(({ key, label, description, icon: PermIcon }) => {
                        const enabled = invitePermissions[key];
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() =>
                              setInvitePermissions((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }))
                            }
                            className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                              enabled
                                ? "bg-brand-light border-brand-light"
                                : "bg-white border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <div className="mt-0.5 flex-shrink-0">
                              <Toggle checked={enabled} onChange={() => {}} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <PermIcon
                                  className={`h-3.5 w-3.5 flex-shrink-0 ${
                                    enabled ? "text-brand" : "text-slate-400"
                                  }`}
                                />
                                <span
                                  className={`text-sm font-medium ${
                                    enabled ? "text-brand" : "text-slate-600"
                                  }`}
                                >
                                  {label}
                                </span>
                              </div>
                              <p className="text-xs text-slate-400 mt-0.5 leading-tight">{description}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {inviteError && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{inviteError}</span>
                </div>
              )}

              {showCostConfirm && prorationData ? (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <DollarSign className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-blue-900">
                          Adding a team member will adjust your billing
                        </h4>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <p className="text-xs text-blue-600 font-medium">Seats</p>
                            <p className="text-sm text-blue-900 font-bold">
                              {prorationData.currentSeats} &rarr; {prorationData.newSeats}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-blue-600 font-medium">Monthly Change</p>
                            <p className="text-sm text-blue-900 font-bold">
                              +${prorationData.monthlyDelta}/mo
                            </p>
                          </div>
                          {prorationData.proratedAmount > 0 && (
                            <div className="col-span-2">
                              <p className="text-xs text-blue-600 font-medium">Prorated Charge Today</p>
                              <p className="text-sm text-blue-900 font-bold">
                                ${(prorationData.proratedAmount / 100).toFixed(2)}
                              </p>
                            </div>
                          )}
                          {prorationData.nextBillAmount != null && (
                            <div className="col-span-2">
                              <p className="text-xs text-blue-600 font-medium">Next Full Bill</p>
                              <p className="text-sm text-blue-900 font-bold">
                                ${(prorationData.nextBillAmount / 100).toFixed(2)}/mo
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={doInvite}
                      disabled={inviting}
                      className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl text-sm font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {inviting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Confirm & Invite"
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowCostConfirm(false);
                        setProrationData(null);
                      }}
                      className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleInvite}
                    disabled={inviting || loadingProration || !inviteName.trim() || !inviteEmail.trim()}
                    className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl text-sm font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingProration ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Checking billing...
                      </>
                    ) : inviting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        Send Invitation
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowInvite(false);
                      setInviteError(null);
                      setShowPermissions(false);
                    }}
                    className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search & Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm bg-white"
          />
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1">
          {["all", "active", "invited", "disabled"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                statusFilter === status
                  ? "bg-brand text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Members List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : members.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No team members found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Member</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Role</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Status</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Permissions</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((member) => {
                const statusBadge = STATUS_BADGES[member.status] || STATUS_BADGES.active;
                const isCurrentUser = member._id === userData?._id;
                const permCount = member.permissions
                  ? Object.values(member.permissions).filter(Boolean).length
                  : 0;

                return (
                  <tr key={member._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-brand-light flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-brand">
                            {member.name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">
                            {member.name}
                            {isCurrentUser && (
                              <span className="ml-2 text-[10px] font-bold text-brand bg-brand-light px-1.5 py-0.5 rounded">
                                YOU
                              </span>
                            )}
                            {String(member._id) === String(firmData?.adminUserId) && (
                              <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                                <Crown className="h-2.5 w-2.5" />
                                OWNER
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-600 capitalize">
                        {String(member._id) === String(firmData?.adminUserId) ? "Owner" : (member.role || member.roleIds?.map((r: any) => r.name || r).join(", ") || " - ")}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${statusBadge.className}`}
                      >
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-slate-500">
                        {permCount} of {PERMISSION_LABELS.length}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!isCurrentUser && (
                        <div className="relative inline-block">
                          <button
                            onClick={() => setMenuOpen(menuOpen === member._id ? null : member._id)}
                            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                          >
                            {actionLoading === member._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreVertical className="h-4 w-4" />
                            )}
                          </button>

                          {menuOpen === member._id && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl border border-slate-200 shadow-lg z-20 py-1">
                              {member.status === "invited" && (
                                <button
                                  onClick={() => handleResendInvite(member)}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                  Resend Invitation
                                </button>
                              )}
                              {member.status === "active" && (
                                <button
                                  onClick={() => handleDeactivate(member._id)}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <UserX className="h-3.5 w-3.5" />
                                  Deactivate
                                </button>
                              )}
                              {member.status === "disabled" && (
                                <button
                                  onClick={() => handleReactivate(member._id)}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 transition-colors"
                                >
                                  <UserCheck className="h-3.5 w-3.5" />
                                  Reactivate
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Transfer Ownership Section */}
      {isOwner && (
        <div className="mt-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <ArrowRightLeft className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-800">Transfer Ownership</h3>
                <p className="text-sm text-slate-500">
                  Transfer firm ownership to another active team member
                </p>
              </div>
            </div>

            {!showTransfer ? (
              <button
                onClick={() => setShowTransfer(true)}
                className="mt-4 px-4 py-2.5 text-sm font-medium text-amber-700 border border-amber-200 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors"
              >
                Transfer Ownership...
              </button>
            ) : (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-4 space-y-4"
              >
                {/* Warning */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-red-800">
                        This action cannot be easily undone
                      </h4>
                      <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                        <li>The new owner will control the firm's subscription and billing</li>
                        <li>The new owner can manage all team members and settings</li>
                        <li>Your role will be changed to Partner</li>
                        <li>Only the new owner can transfer ownership back to you</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Target selection */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">
                    Transfer to
                  </label>
                  <SearchableSelect
                    options={members
                      .filter((m) => m.status === "active" && m._id !== userData?._id)
                      .map((m) => ({
                        value: m._id,
                        label: m.name,
                        description: m.email,
                      }))}
                    value={transferTarget || ""}
                    onChange={(val) => setTransferTarget(val || null)}
                    placeholder="Select a team member..."
                    searchPlaceholder="Search members..."
                    icon={Users}
                  />
                </div>

                {/* Confirmation text */}
                {transferTarget && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">
                      Type <span className="font-bold text-red-600">TRANSFER</span> to confirm
                    </label>
                    <input
                      type="text"
                      value={transferConfirmText}
                      onChange={(e) => setTransferConfirmText(e.target.value)}
                      placeholder="Type TRANSFER to confirm"
                      className="w-full px-4 py-2.5 rounded-xl border border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all text-sm"
                    />
                  </div>
                )}

                {transferError && (
                  <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{transferError}</span>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleTransferOwnership}
                    disabled={transferring || !transferTarget || transferConfirmText !== "TRANSFER"}
                    className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {transferring ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Transferring...
                      </>
                    ) : (
                      <>
                        <ArrowRightLeft className="h-4 w-4" />
                        Transfer Ownership
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowTransfer(false);
                      setTransferTarget(null);
                      setTransferConfirmText("");
                      setTransferError(null);
                    }}
                    className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      )}

            {/* Click away to close menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(null)} />
      )}
    </div>
  );
};

export default UserManagementPage;
